import { useIntl } from 'react-intl';
import styled from 'styled-components';

import type { JobRecentSearchFragment } from '@xing-com/crate-jobs-graphql';
import { useJoinFilters } from '@xing-com/crate-jobs-hooks';
import { lineClamp } from '@xing-com/crate-utils-line-clamp';
import { IconClock } from '@xing-com/icons';
import { mediaConfined } from '@xing-com/layout-tokens';
import {
  scale090,
  scale110,
  spaceS,
  spaceXXS,
  xdlColorText,
} from '@xing-com/tokens';
import { BodyCopy, Meta } from '@xing-com/typography';

type Props = {
  recentSearch: JobRecentSearchFragment;
};

export const RecentSearch: React.FC<Props> = ({ recentSearch }) => {
  const { formatMessage } = useIntl();

  const body = recentSearch?.body;
  const joinedFilters = useJoinFilters(body?.filterCollection ?? [], {
    location: body?.location?.city?.name || body?.location?.text,
    radius: body?.location?.radius,
  });

  const title =
    body?.keywords ?? formatMessage({ id: 'JOBS_RECENT_SEARCH_EMPTY' });
  const fullText = joinedFilters ? [title, joinedFilters].join(' + ') : title;

  return (
    <Wrapper title={fullText}>
      <Icon />
      <TextWrapper>
        <Title noMargin lineHeight="dense">
          {title}
        </Title>
        {joinedFilters ? (
          <Meta size="small" ellipsis noMargin>
            {joinedFilters}
          </Meta>
        ) : null}
      </TextWrapper>
    </Wrapper>
  );
};

const Wrapper = styled.div`
  display: flex;
  gap: ${spaceS};
  color: ${xdlColorText};

  @media ${mediaConfined} {
    gap: 10px;
  }
`;

const TextWrapper = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: ${spaceXXS};
  overflow: hidden;
`;

const Title = styled(BodyCopy)`
  ${lineClamp(2)};

  @media ${mediaConfined} {
    ${lineClamp(1)}
  }
`;

const Icon = styled(IconClock)`
  flex-shrink: 0;
  width: ${scale090};
  height: ${scale090};

  @media ${mediaConfined} {
    width: ${scale110};
    height: ${scale110};
  }
`;
